// uiarc: badge (https://uiarc.dev/r/badge.json) — Base UI + Tailwind'e çevrildi. Davranış uiarc ile aynı: hap
// içindeki metin/ikon değişince eski yazı yukarı çıkıp solar, yenisi bulanıklıktan yükselir ve hap genişliği yayla
// yeni boya uyar. Pasif yeniden akışlar (font, gizli ebeveyn) genişliği anında izler. `prefers-reduced-motion`
// altında yalnız kısa solma. Tonlar: neutral | success | info | warning | danger (uiarc `--accent` = marka rengi).
// Eski `variant` adları uiarc tonuna eşlenir: default→info, secondary→neutral, destructive→danger,
// outline→tonsuz uiarc taban rozeti; `ghost` neutral, `link` outline gibidir.
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  useReducedMotion,
} from 'motion/react'
import type {
  AnimationPlaybackControls,
  HTMLMotionProps,
  TargetAndTransition,
  Transition,
} from 'motion/react'
import { isValidElement, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

type BadgeTone = 'neutral' | 'success' | 'info' | 'warning' | 'danger'

const neutral =
  '[--badge-color:var(--text-secondary)] [--badge-foreground:var(--text-secondary)] [--badge-background:var(--surface-muted)] [--badge-border:var(--border)]'
const success =
  '[--badge-color:var(--success)] [--badge-foreground:var(--success)] [--badge-background:color-mix(in_oklab,var(--success)_10%,var(--surface))] [--badge-border:color-mix(in_oklab,var(--success)_25%,var(--border))]'
const info =
  '[--badge-color:var(--brand)] [--badge-foreground:var(--brand-strong)] [--badge-background:var(--brand-subtle)] [--badge-border:color-mix(in_oklab,var(--brand)_24%,var(--border))]'
const warning =
  '[--badge-color:var(--warning)] [--badge-foreground:var(--warning)] [--badge-background:color-mix(in_oklab,var(--warning)_11%,var(--surface))] [--badge-border:color-mix(in_oklab,var(--warning)_27%,var(--border))]'
const danger =
  '[--badge-color:var(--danger)] [--badge-foreground:var(--danger)] [--badge-background:color-mix(in_oklab,var(--danger)_10%,var(--surface))] [--badge-border:color-mix(in_oklab,var(--danger)_26%,var(--border))]'

// Yükseklik `min-height` yerine `h-` (çağıranlar `h-4` gibi sınıflarla ezebilsin; Button ile aynı gerekçe).
const badgeVariants = cva(
  'group/badge ease-standard inline-flex w-fit shrink-0 items-center overflow-clip rounded-pill border border-[color:var(--badge-border,var(--border))] bg-[var(--badge-background,var(--surface))] text-[color:var(--badge-foreground,var(--foreground))] [line-height:1] font-medium tracking-[-0.01em] whitespace-nowrap transition-[border-color,background-color,color] duration-(--duration-standard) hover:border-[color:color-mix(in_oklab,var(--badge-color,var(--foreground))_32%,var(--border))] hover:bg-[color-mix(in_oklab,var(--badge-color,var(--foreground))_10%,var(--surface))] motion-reduce:duration-(--duration-instant) [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-3',
  {
    variants: {
      variant: {
        neutral,
        success,
        info,
        warning,
        danger,
        // Eski adlar (16 çağrı yeri): uiarc tonuna eşlenir.
        default: info,
        secondary: neutral,
        destructive: danger,
        ghost: neutral,
        outline: '',
        link: '',
      },
      size: {
        sm: 'h-5.5 px-2 text-[11px]',
        md: 'h-6.5 px-2.5 text-xs',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'md',
    },
  },
)

const exitFast: Transition = {
  duration: motionTokens.duration.fast,
  ease: motionTokens.ease.standard,
}
const textIn: TargetAndTransition = {
  opacity: 0,
  y: '0.3em',
  filter: `blur(${motionTokens.blur.soft}px)`,
}
const textOut: TargetAndTransition = {
  opacity: 0,
  y: '-0.3em',
  filter: `blur(${motionTokens.blur.subtle}px)`,
  transition: exitFast,
}
const iconIn: TargetAndTransition = {
  opacity: 0,
  scale: 0.6,
  filter: `blur(${motionTokens.blur.subtle}px)`,
}
const shown: TargetAndTransition = { opacity: 1, y: '0em', scale: 1, filter: 'blur(0px)' }
const fadeOnly: TargetAndTransition = {
  opacity: 0,
  transition: { duration: motionTokens.duration.instant },
}

/** Çıkmakta olan kopyalar solarken yardımcı teknolojiden gizlenir; yalnız güncel metin okunur. */
function Swap(props: HTMLMotionProps<'span'>) {
  const present = useIsPresent()
  return <motion.span {...props} aria-hidden={present ? props['aria-hidden'] : true} />
}

/** Yeni bir ikon bileşeni çapraz geçer; aynı ikonu yeniden çizmek yerinde kalır. */
function iconKey(icon: ReactNode) {
  if (!isValidElement(icon)) return 'icon'
  const type = icon.type as string | { displayName?: string; name?: string }
  return typeof type === 'string' ? type : (type.displayName ?? type.name ?? 'icon')
}

function Badge({
  className,
  variant,
  tone,
  size = 'md',
  icon,
  children,
  render,
  ...props
}: Omit<useRender.ComponentProps<'span'>, 'children'> &
  Omit<VariantProps<typeof badgeVariants>, 'size'> & {
    /** uiarc tonu; verilirse `variant`ın önüne geçer. */
    tone?: BadgeTone
    size?: 'sm' | 'md'
    icon?: ReactNode
    children?: ReactNode
  }) {
  const reduce = useReducedMotion()
  const text =
    typeof children === 'string' || typeof children === 'number' ? String(children) : null
  const glyphKey = icon ? iconKey(icon) : ''
  const body = useRef<HTMLSpanElement>(null)
  const content = useRef<HTMLSpanElement>(null)
  // Genişlik durgunken auto. Yalnız yeni etiket/ikon eski boydan yeni boya yayla gider; pasif yeniden akışlar anında izler.
  const width = useMotionValue<number | 'auto'>('auto')
  const changedAt = useRef(0)
  useLayoutEffect(() => {
    changedAt.current = performance.now()
  }, [text, glyphKey])
  useEffect(() => {
    const node = content.current
    if (!node || typeof ResizeObserver === 'undefined') return
    let last: number | undefined
    let controls: AnimationPlaybackControls | undefined
    const settle = () => {
      width.jump('auto')
      if (body.current) body.current.style.width = 'auto'
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = entry.borderBoxSize?.[0]?.inlineSize ?? node.offsetWidth
      const current = width.get()
      const from = typeof current === 'number' ? current : last
      last = next
      controls?.stop()
      if (
        reduce ||
        from === undefined ||
        from === next ||
        performance.now() - changedAt.current > 120
      )
        return settle()
      // Eski genişliği bu kare boyanmadan sabitle, sonra yenisine yayla git.
      if (body.current) body.current.style.width = `${from}px`
      controls = animate(width, [from, next], { ...motionTokens.spring.morph, onComplete: settle })
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      controls?.stop()
    }
  }, [width, reduce])

  const enter: Transition = reduce
    ? { duration: motionTokens.duration.instant }
    : { duration: motionTokens.duration.standard, ease: motionTokens.ease.enter }

  const inner = (
    <motion.span ref={body} className="block" style={{ width }}>
      <span
        ref={content}
        className="relative inline-flex w-max items-center gap-[5px] group-data-[size=sm]/badge:gap-1"
      >
        {icon ? (
          <span
            className="relative inline-flex flex-none items-center justify-center leading-none text-[color:var(--badge-color,var(--foreground))]"
            aria-hidden="true"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <Swap
                key={glyphKey}
                className="relative inline-flex items-center"
                initial={reduce ? { opacity: 0 } : iconIn}
                animate={shown}
                exit={reduce ? fadeOnly : { ...iconIn, transition: exitFast }}
                transition={reduce ? enter : motionTokens.spring.snappy}
              >
                {icon}
              </Swap>
            </AnimatePresence>
          </span>
        ) : null}
        {text === null ? (
          children
        ) : (
          <span className="relative inline-flex items-center">
            <AnimatePresence mode="popLayout" initial={false}>
              <Swap
                key={text}
                className="block"
                initial={reduce ? { opacity: 0 } : textIn}
                animate={shown}
                exit={reduce ? fadeOnly : textOut}
                transition={enter}
              >
                {text}
              </Swap>
            </AnimatePresence>
          </span>
        )}
      </span>
    </motion.span>
  )

  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant: tone ?? variant, size }), className),
        children: inner,
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      variant: tone ?? variant,
      size,
    },
  })
}

export { Badge, badgeVariants, type BadgeTone }
